import React, { useState, useMemo } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';
import { it } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { Prenotazione } from '../hooks/usePrenotazioni';

interface CalendarViewProps {
  prenotazioni: Prenotazione[];
  onDateSelect: (date: Date) => void;
  selectedDate: Date | null;
}

const CalendarView: React.FC<CalendarViewProps> = ({ prenotazioni, onDateSelect, selectedDate }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Get prenotazioni for a specific date
  const getPrenotazioniForDate = (date: Date) => {
    const dateString = format(date, 'yyyy-MM-dd');
    return prenotazioni.filter(p => p.data === dateString);
  };

  // Get status summary for a date
  const getStatusSummary = (date: Date) => {
    const prenotazioniForDate = getPrenotazioniForDate(date);
    const confermate = prenotazioniForDate.filter(p => p.stato === 'confermata').length;
    const inAttesa = prenotazioniForDate.filter(p => p.stato === 'in_attesa').length;
    const rifiutate = prenotazioniForDate.filter(p => p.stato === 'rifiutata').length;
    
    return { confermate, inAttesa, rifiutate, total: prenotazioniForDate.length };
  };

  // Calendar days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentMonth]);

  // Navigation
  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

  // Check if date is today
  const isToday = (date: Date) => isSameDay(date, new Date());

  // Get dot color based on status
  const getDotColor = (date: Date) => {
    const { confermate, inAttesa, rifiutate } = getStatusSummary(date);
    
    if (rifiutate > 0) return 'bg-red-500';
    if (confermate > 0) return 'bg-green-500';
    if (inAttesa > 0) return 'bg-orange-500';
    return '';
  };

  // Get border color for selected date
  const getBorderColor = (date: Date) => {
    const hasBookings = getPrenotazioniForDate(date).length > 0;
    const isSelected = selectedDate && isSameDay(date, selectedDate);
    
    if (isSelected) return 'ring-2 ring-orange-500';
    if (hasBookings) return 'ring-1 ring-orange-300';
    return '';
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
      {/* Calendar */}
      <div className="flex-1 min-w-0">
        <div className="bg-white rounded-lg shadow p-3 sm:p-6">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <div className="hidden sm:flex items-center space-x-2">
              <CalendarIcon className="h-6 w-6 text-gray-600" />
              <h2 className="text-xl font-semibold text-gray-900">Calendario</h2>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4 w-full sm:w-auto justify-between sm:justify-end">
              <button
                onClick={prevMonth}
                className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="text-sm sm:text-lg font-medium text-gray-900 capitalize">
                {format(currentMonth, 'MMMM yyyy', { locale: it })}
              </span>
              <button
                onClick={nextMonth}
                className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-px sm:gap-1">
            {/* Day headers */}
            {['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'].map((day) => (
              <div key={day} className="p-1 sm:p-3 text-center text-[10px] sm:text-sm font-medium text-gray-500 uppercase">
                {day}
              </div>
            ))}

            {/* Calendar days */}
            {calendarDays.map((day) => {
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const statusSummary = getStatusSummary(day);
              const hasBookings = statusSummary.total > 0;
              const dotColor = getDotColor(day);
              const borderColor = getBorderColor(day);

              return (
                <div
                  key={day.toISOString()}
                  onClick={() => onDateSelect(day)}
                  className={`
                    relative p-1 sm:p-3 min-h-[48px] sm:min-h-[80px] cursor-pointer transition-all
                    ${isCurrentMonth ? 'bg-white' : 'bg-gray-50'}
                    ${borderColor}
                    ${isToday(day) ? 'ring-2 ring-red-500' : ''}
                    hover:bg-gray-50
                  `}
                >
                  <div className={`
                    text-xs sm:text-sm font-medium mb-0.5 sm:mb-1
                    ${isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}
                    ${isToday(day) ? 'text-red-600' : ''}
                  `}>
                    {format(day, 'd')}
                  </div>

                  {hasBookings && (
                    <div className="text-[9px] sm:text-xs text-gray-600 mb-0.5 sm:mb-1">
                      {statusSummary.total}
                    </div>
                  )}

                  {dotColor && (
                    <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${dotColor} mx-auto`} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-gray-200">
            <div className="flex flex-wrap gap-3 sm:gap-4 text-[10px] sm:text-xs">
              <div className="flex items-center space-x-1.5">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-gray-600">Confermate</span>
              </div>
              <div className="flex items-center space-x-1.5">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span className="text-gray-600">Da accettare</span>
              </div>
              <div className="flex items-center space-x-1.5">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span className="text-gray-600">Rifiutate</span>
              </div>
              <div className="flex items-center space-x-1.5">
                <div className="w-3 h-3 border-2 border-red-500 rounded"></div>
                <span className="text-gray-600">Oggi</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <div className="w-full lg:w-80">
        <div className="bg-white rounded-lg shadow p-3 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
            {selectedDate ? format(selectedDate, 'EEEE d MMMM', { locale: it }) : 'Seleziona una data'}
          </h3>

          {selectedDate ? (
            getPrenotazioniForDate(selectedDate).length > 0 ? (
              <div className="space-y-2 sm:space-y-3">
                {getPrenotazioniForDate(selectedDate).map((prenotazione) => (
                  <div key={prenotazione.id} className="p-2.5 sm:p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                      <h5 className="font-medium text-gray-900 text-sm">{prenotazione.nome}</h5>
                      <span className={`px-2 py-0.5 text-[10px] sm:text-xs rounded-full ${
                        prenotazione.stato === 'confermata' ? 'bg-green-100 text-green-800' :
                        prenotazione.stato === 'rifiutata' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {prenotazione.stato === 'confermata' ? 'Confermata' :
                         prenotazione.stato === 'rifiutata' ? 'Rifiutata' : 'In Attesa'}
                      </span>
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600 space-y-0.5">
                      <div>Ora: {prenotazione.ora} · Persone: {prenotazione.persone}</div>
                      <div className="truncate">{prenotazione.email}</div>
                      <div>{prenotazione.telefono}</div>
                      {prenotazione.note && (
                        <div className="text-gray-500 truncate">Note: {prenotazione.note}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">Nessuna prenotazione</p>
            )
          ) : (
            <p className="text-gray-500 text-sm">Tocca una data per vedere le prenotazioni</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarView; 